// Payment Plugin Registry
// Manages payment gateway plugins and their availability

import type { CheckoutRequest } from "@/types/woocommerce.types";
import {
  PAYMENT_ERROR_CODES,
  type PAYMENT_ERROR_TYPES,
} from "@/lib/constants/payment-error-constants";
import { log_gateway_event } from "@/lib/observability/sentry-reporting";

export interface PaymentProcessingResult {
  success: boolean;
  order_id?: string | number;
  error_code?: PAYMENT_ERROR_TYPES;
  message?: string;
  redirect_url?: string;
  requires_3d_secure?: boolean;
}

export interface PaymentPlugin {
  id: string;
  country_rules: {
    allowed_countries?: string[];
    banned_countries?: string[];
  };
  process_payment(
    order_data: CheckoutRequest,
  ): Promise<PaymentProcessingResult>;
  initialize_ui?(): Promise<void>;
}

// Plugin storage
const plugins: Map<string, PaymentPlugin> = new Map();

/**
 * Registers a payment plugin
 */
export const register_plugin = (plugin: PaymentPlugin): void => {
  plugins.set(plugin.id, plugin);
};

/**
 * Gets a plugin by its ID
 */
export const get_plugin_by_id = (id: string): PaymentPlugin | null => {
  return plugins.get(id) || null;
};

/**
 * Checks if a gateway is allowed for a specific country
 */
export const is_gateway_allowed = (
  gateway_id: string,
  country_code: string | null,
): boolean => {
  const gateway = get_plugin_by_id(gateway_id);

  if (!gateway || !country_code) {
    return false;
  }

  const { allowed_countries, banned_countries } = gateway.country_rules;

  // If whitelist exists (not undefined/null)
  if (allowed_countries !== undefined) {
    // Empty whitelist means no countries are allowed
    if (allowed_countries.length === 0) {
      return false;
    }
    // Non-empty whitelist means only those countries are allowed
    return allowed_countries.includes(country_code);
  }

  // If no whitelist but blacklist exists, allow everything except blacklisted
  if (banned_countries && banned_countries.length > 0) {
    return !banned_countries.includes(country_code);
  }

  // No whitelist and no blacklist means everything is allowed
  return true;
};

export const normalize_order_creation_error = ({
  gateway,
  result,
}: {
  gateway: string;
  result: { code?: string; message?: string } | null | undefined;
}): PaymentProcessingResult => {
  const valid_codes = Object.values(PAYMENT_ERROR_CODES);

  const is_known_woocommerce_code = valid_codes.includes(
    result?.code as PAYMENT_ERROR_TYPES,
  );

  const error_code: PAYMENT_ERROR_TYPES = is_known_woocommerce_code
    ? (result?.code as PAYMENT_ERROR_TYPES)
    : PAYMENT_ERROR_CODES.ORDER_CREATION_FAILED;

  const log_level = is_known_woocommerce_code ? "warn" : "error";

  log_gateway_event(
    log_level,
    gateway,
    "order_creation_failed",
    result?.message || "Order creation failed.",
    { error_code, woocommerce_code: result?.code },
  );

  return {
    success: false,
    error_code,
    message: result?.message || "Order creation failed.",
  };
};
